<template>
  <div>
    <H3TitleView class="title mb-4 ml-1 small-title"> {{ title }}</H3TitleView>
    <b-message v-if="actualites.length === 0" type="is-waring">
      {{ $t("actualitesPage.no-actu") }}
    </b-message>
    <div class="actualites-card-content">
      <LoadingSkeleton v-if="isLoadingData" :loading-text="$t('actualitesPage.loading')" />
      <ActualiteCard
        v-for="(actualite, i) in actualites"
        v-else
        :key="i"
        :actualite="actualite"
        :class="{ 'mt-3': i > 0 }"
        @read-more="openModale"
      />
    </div>

    <b-modal
      v-model="open"
      has-modal-card
      trap-focus
      scroll="keep"
      aria-role="dialog"
      aria-label="actualitesCardsView"
      :aria-modal="true"
      class="actualitesCardsView-modale"
      @cancel="open = false"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <H3TitleView v-if="actualiteSelected.grae" class="modal-card-title"
            >{{ $t("projet.en-bref.actu-regionale") }}
          </H3TitleView>
          <H3TitleView v-else class="modal-card-title"
            >{{ $t("projet.en-bref.actu-nationale") }}
          </H3TitleView>
          <div class="ActualitesModalView-infos">
            {{
              $t("projet.en-bref.postee-le", [formatDate(actualiteSelected.dateDebutPublication)])
            }}
          </div>
        </header>
        <section class="modal-card-body">
          <H3TitleView>{{ actualiteSelected.titre }}</H3TitleView>
          <hr />
          <p
            v-html="
              actualiteSelected.contenu ? actualiteSelected.contenu.replaceAll('\n', '<br/>') : ''
            "
          ></p>
        </section>

        <footer class="modal-card-foot">
          <div class="buttons">
            <div class="hoverable-discrete-link mr-3" @click="open = false">
              {{ $t("close-generic") }}
            </div>
          </div>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { ActualiteDTO } from "@/model/bean/GeneratedDTOs"
import H3TitleView from "@/views/commons/H3TitleView.vue"
import ActualiteCard from "@/pages/actualite/ActualiteCard.vue"
import LoadingSkeleton from "@/views/generic_components/LoadingSkeleton.vue"
import { BMessage, BModal } from "buefy"
import DateUtils from "@/services/DateUtils"

export default defineComponent({
  name: "ActualitesCardsView",
  components: { LoadingSkeleton, ActualiteCard, H3TitleView, BMessage, BModal },
  props: {
    isLoadingData: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    actualites: {
      type: Array as PropType<ActualiteDTO[]>,
      required: true,
    },
  },
  data() {
    return {
      actualiteSelected: new ActualiteDTO(),
      open: false,
    }
  },
  methods: {
    formatDate(date: Date): string {
      return DateUtils.formatDate(date)
    },

    openModale(actuId: string): void {
      const actuFound = this.actualites.find((a) => a.id === actuId)
      if (!actuFound) {
        return
      }
      this.actualiteSelected = actuFound
      this.open = true
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

$pp-actu-padding-hor: 1rem;
$pp-actu-padding-vert: 0.3rem;

h3-title-view.title {
  min-width: 230px;
}

.actualites-card-content {
  max-height: none;
  overflow-y: hidden;
}

.small-title {
  font-size: 19px;
}

.actualitesCardsView-actualite {
  width: 100%;
  height: 100%;
  display: flex;
}

.actualitesCardsView-actualite-content {
  width: 100%;
  padding: $pp-actu-padding-vert $pp-actu-padding-hor;
  position: relative;
}

.actualitesCardsView-actualite-date {
  font-size: 0.7rem;
  padding-top: 2.7rem;
  padding-left: $tag-margin-left;
  padding-right: $pp-actu-padding-hor;
  width: 150px;
  font-style: italic;
}

.actualitesCardsView-actualite-message {
  text-align: left;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

.actualitesCardsView-actualite-link {
  position: absolute;
  bottom: calc(#{$pp-actu-padding-vert} - #{$tag-height});
  right: $pp-actu-padding-hor;
}

.actualitesCardsView-modale {
  &.modal .modal-card {
    height: auto;
    max-height: 80vh;
    width: auto;
    min-width: 30vw;
    max-width: 40vw;
    overflow-y: auto !important;
  }

  .animation-content {
    height: 100%;
  }
}

$card-width: 450px;
$card-height: 120px;
.custom-card {
  width: $card-width;
  height: $card-height;
  border-radius: 10px;
  border: 1px solid $bookmarkcard-border-color;
}

.modal-card-body .h3-title-view,
.modal-card-body p {
  display: block;
  overflow-wrap: break-word;
  word-break: break-word;
}
</style>
